import styled, { css } from "styled-components";
import {
  PanelGroup,
  PanelResizeHandle,
  type PanelGroupProps,
} from "react-resizable-panels";
import { colors, motion } from "../../theme-v2";

/**
 * Re-export `Panel` as `SplitPanelItem`. The library's Panel API
 * (defaultSize, minSize, maxSize, collapsible, …) is already
 * well-shaped — no need to wrap it.
 *
 * Consumer pattern:
 *
 *   <SplitPanel direction="vertical">
 *     <SplitPanelItem defaultSize={60} minSize={30}>...</SplitPanelItem>
 *     <SplitResizeHandle />
 *     <SplitPanelItem defaultSize={40} minSize={20}>...</SplitPanelItem>
 *   </SplitPanel>
 */
export { Panel as SplitPanelItem } from "react-resizable-panels";
export type { PanelProps as SplitPanelItemProps } from "react-resizable-panels";

export interface SplitPanelProps extends Omit<PanelGroupProps, "direction"> {
  /**
   * Resize axis. `vertical` = top/bottom split (the Knowledge Hub
   * sidebar's stack). `horizontal` = left/right split (the doc + sidebar
   * shell).
   */
  direction: "horizontal" | "vertical";
}

/**
 * SplitPanel — themed wrapper around `react-resizable-panels`'s
 * `PanelGroup`. Wraps Panels with a resize handle that picks up
 * theme-v2 colors and gives the otherwise-invisible 0px library
 * default a real hit area.
 */
export const SplitPanel = ({
  direction,
  children,
  ...rest
}: SplitPanelProps) => (
  <PanelGroup direction={direction} {...rest}>
    {children}
  </PanelGroup>
);

export interface SplitResizeHandleProps {
  /** Required when the parent SplitPanel uses `autoSaveId`. */
  id?: string;
  className?: string;
  disabled?: boolean;
}

/**
 * Resize handle between SplitPanelItems. Renders an 8px hit area with
 * a 1px subtle line; line highlights to accent on hover/drag.
 * Orientation is inherited from the parent group via the
 * `data-panel-group-direction` attribute the library sets.
 */
export const SplitResizeHandle = ({
  id,
  className,
  disabled,
}: SplitResizeHandleProps) => (
  <StyledHandle id={id} className={className} disabled={disabled}>
    <StyledHandleLine />
  </StyledHandle>
);

const horizontalHandle = css`
  width: 8px;
  cursor: col-resize;
  flex-direction: row;

  > span {
    width: 1px;
    height: 100%;
  }
`;

const verticalHandle = css`
  height: 8px;
  cursor: row-resize;
  flex-direction: column;

  > span {
    width: 100%;
    height: 1px;
  }
`;

const StyledHandle = styled(PanelResizeHandle)`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  position: relative;
  z-index: 1;

  &[data-panel-group-direction="horizontal"] {
    ${horizontalHandle}
  }
  &[data-panel-group-direction="vertical"] {
    ${verticalHandle}
  }

  &:hover > span,
  &[data-resize-handle-active] > span {
    background: ${colors.accent.base};
  }
`;

const StyledHandleLine = styled.span`
  background: ${colors.border.subtle};
  display: block;
  transition: background ${motion.duration.fast} ${motion.easing.standard};
`;
